import _extends from "@babel/runtime/helpers/extends";
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, memo, useMemo } from 'react';
import merge from 'lodash/merge';
import isEqual from 'lodash/isEqual';
import mapValues from 'lodash/mapValues';
import { init } from "../i18n/i18n";
import LocaleContext from "../context/LocaleContext";
import { getRandomKey } from "../utils";
// based on https://github.com/mridgway/hoist-non-react-statics/blob/master/src/index.js
var hoistBlackList = {
  $$typeof: true,
  render: true,
  compare: true,
  type: true
};
function copyStaticProperties(base, target) {
  Object.keys(base).forEach(function (key) {
    if (!hoistBlackList[key]) {
      Object.defineProperty(target, key,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      Object.getOwnPropertyDescriptor(base, key));
    }
  });
}

// const cacheComponents = new WeakMap();
var cacheI18nInstance = {};

// with no hoc

//  fc with ref

// fc & Legacy component

function withI18n(
// FIXME 处理被包裹函数的类型定义
WrappedComponent, params) {
  var _ref = params || {},
    _ref$forwardRef = _ref.forwardRef,
    forwardRef = _ref$forwardRef === void 0 ? true : _ref$forwardRef,
    _ref$_noHoc = _ref._noHoc,
    _noHoc = _ref$_noHoc === void 0 ? false : _ref$_noHoc,
    _ref$noMemo = _ref.noMemo,
    noMemo = _ref$noMemo === void 0 ? false : _ref$noMemo,
    _ref$componentName = _ref.componentName,
    componentName = _ref$componentName === void 0 ? '' : _ref$componentName,
    _ref$no$i18nProps = _ref.no$i18nProps,
    no$i18nProps = _ref$no$i18nProps === void 0 ? false : _ref$no$i18nProps,
    locale = _ref.locale,
    lang = _ref.lang;
  var baseComponentName = componentName || WrappedComponent.displayName || WrappedComponent.name;
  // 如果未指定componentName，则使用baseComponentName作为第二选择
  var primaryName = baseComponentName || getRandomKey();
  // 只生成实例
  if (_noHoc && baseComponentName && locale) {
    init({
      locale: locale,
      componentName: baseComponentName
    });
    return WrappedComponent;
  }
  function _inwrap(props) {
    var theContext = useContext(LocaleContext);
    // 二次memo，避免多次初始化
    var paramsLocale = locale;
    var contextLocale = theContext === null || theContext === void 0 ? void 0 : theContext.locale;
    // 优先使用params传入的lang
    var paramsLang = lang;
    // 获取上下文的指定的lang
    var contextLang = theContext === null || theContext === void 0 ? void 0 : theContext.lang;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    var propsLocale = props === null || props === void 0 ? void 0 : props.locale;
    var newI18n = useMemo(function () {
      if (cacheI18nInstance[primaryName]) {
        if (isEqual(cacheI18nInstance[primaryName].paramsLocale, paramsLocale) && isEqual(cacheI18nInstance[primaryName].contextLocale, contextLocale) && isEqual(cacheI18nInstance[primaryName].propsLocale, propsLocale) && isEqual(cacheI18nInstance[primaryName].contextLang, contextLang) && isEqual(cacheI18nInstance[primaryName].paramsLang, paramsLang)) {
          return {
            $i18n: cacheI18nInstance[primaryName].$i18n,
            locale: cacheI18nInstance[primaryName].locale
          };
        }
      }
      // 由于存在传入的locale并不是一个合法的格式 所以需要做一次判断
      var propsLocaleCanHasNoNs = propsLocale && baseComponentName ? mapValues(propsLocale, function (item) {
        var _ref2;
        return item !== null && item !== void 0 && item[baseComponentName] ? item : (_ref2 = {}, _ref2[baseComponentName] = item, _ref2);
      }) : propsLocale;
      // 入参 < 上下文 < 组件props
      var mergeLocale = merge({}, paramsLocale, contextLocale, propsLocaleCanHasNoNs);
      // 入参 < 上下文
      var mergeLang = paramsLang || contextLang;
      var _$i18n = init({
        locale: mergeLocale,
        componentName: baseComponentName,
        lang: mergeLang
      });
      cacheI18nInstance[primaryName] = {
        $i18n: _$i18n,
        paramsLocale: paramsLocale,
        contextLocale: contextLocale,
        propsLocale: propsLocale,
        contextLang: contextLang,
        paramsLang: paramsLang,
        locale: mergeLocale
      };
      return {
        $i18n: _$i18n,
        locale: mergeLocale
      };
    }, [paramsLocale, contextLocale, propsLocale, paramsLang, contextLang]);
    var providerValue = useMemo(function () {
      return {
        locale: newI18n.locale,
        $i18n: newI18n.$i18n
      };
    }, [newI18n.locale, newI18n.$i18n]);
    var theProps = no$i18nProps ? props : _extends({}, props, {
      $i18n: newI18n.$i18n
    });
    return {
      providerValue: providerValue,
      theProps: theProps
    };
  }
  var TheComponent = forwardRef === false ? function (props) {
    var _inwrap2 = _inwrap(props),
      providerValue = _inwrap2.providerValue,
      theProps = _inwrap2.theProps;
    return /*#__PURE__*/React.createElement(LocaleContext.Provider, {
      value: providerValue
    }, /*#__PURE__*/React.createElement(WrappedComponent, theProps));
  } : /*#__PURE__*/React.forwardRef(function (props, ref) {
    var _inwrap3 = _inwrap(props),
      providerValue = _inwrap3.providerValue,
      theProps = _inwrap3.theProps;
    return /*#__PURE__*/React.createElement(LocaleContext.Provider, {
      value: providerValue
    }, /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, theProps, {
      ref: ref
    })));
  });
  if (!noMemo) {
    TheComponent = /*#__PURE__*/memo(TheComponent);
  }
  TheComponent.displayName = baseComponentName;
  copyStaticProperties(WrappedComponent, TheComponent);
  // cacheComponents.set(WrappedComponent, TheComponent);
  return TheComponent;
}
export default withI18n;